import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Sparkles } from "lucide-react";
import type React from "react";
import { useMemo } from "react";

import { ColumnsMenu } from "~/components/admin/columns-menu";
import { AdminDataTable } from "~/components/admin/data-table";
import { EmptyState } from "~/components/ui/empty-state";
import { requirePermission } from "~/lib/functions/admin-access";
import {
  adminNovaUsageSummaryQueryOptions,
  adminNovaWeeklyNumbersQueryOptions,
  type getAdminNovaUsageSummaryFn,
} from "~/lib/functions/admin-nova-usage";
import {
  NOVA_USAGE_COLUMN_IDS,
  NOVA_USAGE_SORT_DEFAULTS,
  NOVA_USAGE_SORTS,
  type NovaUsageColumnId,
  type NovaUsageSort,
  novaUsageSearchSchema,
  resolveNovaUsageTableState,
  sortNovaUsageCoaches,
  toggleNovaUsageColumn,
} from "~/lib/shared/nova-usage-table";
import { nextSortSearch } from "~/lib/shared/sort";

import { NovaWeeklyNumbers } from "./-components/nova-weekly-numbers";

export const Route = createFileRoute("/admin/nova-usage/")({
  staticData: { title: "Nova Usage" },
  validateSearch: novaUsageSearchSchema,
  beforeLoad: ({ context }) =>
    requirePermission(context.adminPermissions, "nova-usage:read"),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(adminNovaUsageSummaryQueryOptions),
      context.queryClient.ensureQueryData(adminNovaWeeklyNumbersQueryOptions),
    ]);
  },
  component: NovaUsageAdminPage,
});

type CoachUsage = Awaited<
  ReturnType<typeof getAdminNovaUsageSummaryFn>
>["coaches"][number];

const COLUMN_LABELS: Record<NovaUsageColumnId, string> = {
  conversations: "Conversations",
  runs: "Runs",
  runCost: "Run cost",
  input: "Input",
  cacheRead: "Cache read",
  cacheWrite: "Cache write",
  output: "Output",
  cost: "Cost",
  models: "Models",
  updated: "Updated",
};

const COACH_COLUMN: ColumnDef<CoachUsage> = {
  id: "coach",
  header: "Coach",
  cell: ({ row }) => {
    const coach = row.original;
    return (
      <>
        <p className="font-medium text-gray-900">{coach.coachName}</p>
        <p className="text-xs text-gray-500">
          {coach.coachEmail} · @{coach.coachUsername}
        </p>
        {coach.legacyInputTokens + coach.legacyOutputTokens > 0 && (
          <p className="mt-1 text-xs text-amber-600">
            Includes legacy aggregate tokens
          </p>
        )}
      </>
    );
  },
};

function figureColumn(
  id: Exclude<NovaUsageColumnId, "models" | "updated">,
  render: (coach: CoachUsage) => string,
): ColumnDef<CoachUsage> {
  return {
    id,
    header: COLUMN_LABELS[id],
    meta: { align: "end" },
    cell: ({ row }) => (
      <span className="tabular-nums">{render(row.original)}</span>
    ),
  };
}

const COLUMN_MENU_GROUPS = [
  {
    columns: NOVA_USAGE_COLUMN_IDS.map((id) => ({
      id,
      label: COLUMN_LABELS[id],
    })),
  },
];

const OPTIONAL_COLUMNS: Record<NovaUsageColumnId, ColumnDef<CoachUsage>> = {
  conversations: figureColumn("conversations", (c) =>
    formatInteger(c.conversationCount),
  ),
  runs: figureColumn("runs", (c) => formatInteger(c.runCount)),
  runCost: figureColumn("runCost", (c) =>
    c.runCostUsd > 0 ? formatUsd(c.runCostUsd) : "—",
  ),
  input: figureColumn("input", (c) => formatInteger(c.inputTokens)),
  cacheRead: figureColumn("cacheRead", (c) =>
    formatInteger(c.cacheReadInputTokens),
  ),
  cacheWrite: figureColumn("cacheWrite", (c) =>
    formatInteger(c.cacheWriteInputTokens),
  ),
  output: figureColumn("output", (c) => formatInteger(c.outputTokens)),
  cost: figureColumn("cost", (c) => formatUsd(c.estimatedCostUsd)),
  models: {
    id: "models",
    header: COLUMN_LABELS.models,
    meta: { className: "max-w-80 text-xs text-gray-500" },
    cell: ({ row }) => formatModelBreakdown(row.original.modelBreakdown),
  },
  updated: {
    id: "updated",
    header: COLUMN_LABELS.updated,
    meta: { className: "text-xs text-gray-500" },
    cell: ({ row }) => formatDate(row.original.updatedAt),
  },
};

function NovaUsageAdminPage(): React.ReactElement {
  const { data } = useQuery(adminNovaUsageSummaryQueryOptions);
  const { data: weeks } = useQuery(adminNovaWeeklyNumbersQueryOptions);
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const state = resolveNovaUsageTableState(search);
  const { sort, direction } = state;

  const coaches = useMemo(
    () => sortNovaUsageCoaches(data?.coaches ?? [], sort, direction),
    [data, sort, direction],
  );
  const columns = useMemo(
    () => [COACH_COLUMN, ...state.columns.map((id) => OPTIONAL_COLUMNS[id])],
    [state.columns],
  );

  const changeSort = (column: NovaUsageSort): void => {
    navigate({
      search: (previous) => ({
        ...previous,
        ...nextSortSearch(
          { sort, direction },
          column,
          NOVA_USAGE_SORT_DEFAULTS,
        ),
      }),
    });
  };

  if (!data) {
    return (
      <div className="space-y-4 p-6">
        <div className="h-7 w-40 animate-pulse rounded bg-gray-100" />
        <div className="grid gap-3 md:grid-cols-5">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className="h-24 animate-pulse rounded-lg border border-gray-200 bg-gray-50"
            />
          ))}
        </div>
      </div>
    );
  }

  const totals = data.totals;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-medium text-gray-900">Nova usage</h1>
        <p className="mt-1 text-sm text-gray-500">
          How coaches use NOVA each week, then token usage, prompt cache
          activity, and estimated model cost grouped by coach account.
        </p>
      </div>

      {weeks && <NovaWeeklyNumbers weeks={weeks} />}

      <div className="mb-6 grid gap-3 md:grid-cols-4 xl:grid-cols-8">
        <UsageStat label="Coaches" value={formatInteger(totals.coachCount)} />
        <UsageStat
          label="Conversations"
          value={formatInteger(totals.conversationCount)}
        />
        <UsageStat label="Runs" value={formatInteger(totals.runCount)} />
        <UsageStat label="Input" value={formatInteger(totals.inputTokens)} />
        <UsageStat
          label="Cache read"
          value={formatInteger(totals.cacheReadInputTokens)}
        />
        <UsageStat
          label="Cache write"
          value={formatInteger(totals.cacheWriteInputTokens)}
        />
        <UsageStat label="Output" value={formatInteger(totals.outputTokens)} />
        <UsageStat
          label="Estimated cost"
          value={formatUsd(totals.estimatedCostUsd)}
        />
      </div>

      {coaches.length > 0 && (
        <div className="mb-3 flex justify-end">
          <ColumnsMenu
            groups={COLUMN_MENU_GROUPS}
            shown={state.columns}
            onToggle={(column) =>
              navigate({
                search: (previous) => ({
                  ...previous,
                  ...toggleNovaUsageColumn(state, column),
                }),
                replace: true,
              })
            }
          />
        </div>
      )}

      <AdminDataTable
        data={coaches}
        columns={columns}
        getRowId={(coach) => coach.coachId}
        sorts={NOVA_USAGE_SORTS}
        sort={sort}
        direction={direction}
        onSortChange={changeSort}
        emptyState={
          <EmptyState
            size="sm"
            icon={Sparkles}
            title="No Nova usage recorded yet"
            description="New chat turns will appear here after Nova responds."
          />
        }
      />
    </div>
  );
}

function UsageStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-2 text-xl font-medium text-gray-900 tabular-nums">
        {value}
      </p>
    </div>
  );
}

function formatInteger(value: number): string {
  return new Intl.NumberFormat("en-US").format(value);
}

function formatUsd(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: value >= 1 ? 2 : 4,
    maximumFractionDigits: value >= 1 ? 2 : 4,
  }).format(value);
}

function formatModelBreakdown(
  breakdown: Array<{
    model: string;
    totalTokens: number;
    cacheReadInputTokens: number;
    cacheWriteInputTokens: number;
  }>,
): string {
  if (breakdown.length === 0) return "Legacy aggregate";
  return breakdown
    .map((entry) => {
      const cacheTokens =
        entry.cacheReadInputTokens + entry.cacheWriteInputTokens;
      const cacheSuffix =
        cacheTokens > 0 ? `, cached ${formatInteger(cacheTokens)}` : "";
      return `${entry.model}: ${formatInteger(entry.totalTokens)}${cacheSuffix}`;
    })
    .join(", ");
}

function formatDate(value: Date | string | null): string {
  if (!value) return "Never";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}
